import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  /* Interior */
  {
    id: "graph1",
    title: "Evolución en el tiempo",
    menuTitle: "Interior",
    text: `En 2024, la mitad de las niñas y niños (54%) que estaban matriculados en la educación parvularia pública o subvencionada pintó o escribió al 
          menos una vez a la semana, en el último mes. A un 36% de las niñas y niños les leyeron cuentos o historias y un 34% cantó o tocó un instrumento. 
          Estas tres actividades se han mantenido relativamente estables en el tiempo.`,
    link: "https://public.flourish.studio/visualisation/23729696/embed",
  },
  {
    id: "graph2",
    title: "Análisis por sexo",
    menuTitle: "\u2022 Sexo",
    text: `En 2024, un 52% de los niños pintó o escribió al menos una vez por semana en el último mes y un 56% de las niñas. Además, a las niñas les leyeron 
          más historias o cuentos (37% de ellas versus 34% de los niños) y con las niñas cantaron o tocaron un instrumento al menos una vez por semana en mayor 
          proporción que con los niños (36% de las niñas y 33% de los niños).`,
    link: "https://public.flourish.studio/visualisation/23731590/embed",
  },
  {
    id: "graph3",
    title: "Análisis por dependencia administrativa",
    menuTitle: "\u2022 Dependencia",
    text: `La dependencia administrativa puede ser utilizada como una aproximación del nivel socioeconómico de los hogares en que viven niñas y niños. 
          La evidencia en Chile da cuenta de que la educación pública tiende a concentrar a estudiantes de mayor vulnerabilidad socioeconómica, mientras 
          que la particular subvencionada presenta una mayor variabilidad y, en algunos casos, una mayor concentración de estudiantes de clase media y alta.[5] 

  Los resultados muestran que, en 2024, niñas y niños de colegios públicos realizan menos actividades junto a sus cuidadores en comparación con los 
          colegios particulares subvencionados: un 49% de las niñas y niños de colegios públicos pintó o escribió al menos una vez por semana, frente a un 57% 
          de las y los estudiantes de colegios subvencionados; un 31% cantó o tocó un instrumento (versus un 36%); y un 32% escuchó cuentos o historias, proporción 
          6 puntos menor que en los colegios subvencionados.`,
    link: "https://public.flourish.studio/visualisation/23732086/embed",
  },
  {
    id: "graph4",
    title: "Análisis por situación de hacinamiento",
    menuTitle: "\u2022 Hacinamiento",
    text: `Cuando las niñas y los niños crecen en hogares con hacinamiento, es decir, más de 2,5 personas por dormitorio, tienen menos oportunidades de 
          realizar actividades como pintar, cantar o escuchar cuentos semanalmente. Mientras que un 56% de las niñas y niños que no viven en hacinamiento 
          pintaron o escribieron al menos una vez por semana, un 49% de las niñas y niños que viven en hacinamiento lo hicieron.`,
    link: "https://public.flourish.studio/visualisation/23732572/embed",
  },
  {
    id: "graph5",
    title: "Análisis por disponibilidad de espacio para jugar",
    menuTitle: "\u2022 Espacio para jugar",
    text: `Las niñas y niños que no cuentan con un espacio para jugar en su hogar, pintan y escriben menos que quienes sí tienen espacios, también cantan 
          y tocan menos instrumentos, y les leen menos cuentos e historias. En los últimos años, ha disminuido la proporción de niñas y niños con espacios 
          adecuados para jugar en sus hogares, reflejando cambios en las condiciones de vida que afectan el ejercicio de su derecho al juego y la recreación.`,
    link: "https://public.flourish.studio/visualisation/23731829/embed",
  },
  {
    id: "graph6",
    title: "Análisis por cercanía de espacios públicos de recreación",
    menuTitle: "\u2022 Cercanía de espacios",
    text: `Las niñas y niños que viven en viviendas que se encuentran más cercanos a espacios de recreación realizan en mayor proporción actividades como 
          pintar, escribir, cantar, tocar instrumentos con una frecuencia de al menos una vez a la semana.`,
    link: "https://public.flourish.studio/visualisation/23732620/embed",
  },
  /* Exterior */
  {
    id: "graph7",
    title: "Evolución en el tiempo",
    menuTitle: "Exterior",
    text: `Los datos de 2024 reflejan un retroceso general en la realización de actividades en el espacio público entre niñas, niños y sus cuidadores. 
          Mientras el deporte regresó a niveles cercanos a 2017 (28%) tras un leve aumento en 2023, el juego con otros niños en el barrio fue la categoría 
          con la mayor caída, disminuyendo 7 puntos respecto a la medición anterior hasta alcanzar sólo a un 25% de la población infantil. A esto se suma 
          la baja en la asistencia a plazas o parques, que descendió 4 puntos (38%). Por su parte, la participación cultural se mantiene como la actividad 
          más rezagada, profundizando una tendencia a la baja que ha sido constante en el tiempo.`,
    link: "https://public.flourish.studio/visualisation/23729914/embed",
  },
  {
    id: "graph8",
    title: "Análisis por sexo",
    menuTitle: "\u2022 Sexo",
    text: `En 2024, los niños participan en mayor proporción de actividades deportivas de forma semanal que las niñas (30% contra un 26%). En el caso de las 
          actividades culturales, la proporción de niñas y niños es similar (13% y 12%, respectivamente). Esta paridad se observa también en el juego de barrio 
          (ambos 25%) y en las visitas a plazas o parques, con cifras muy cercanas (ambos 38%).`,
    link: "https://public.flourish.studio/visualisation/23732169/embed",
  },
  {
    id: "graph9",
    title: "Análisis por dependencia administrativa",
    menuTitle: "\u2022 Dependencia",
    text: `Las niñas y niños que asisten a establecimientos educacionales subvencionados realizan en mayor proporción actividades deportivas en una 
          frecuencia de al menos una vez por semana (en comparación a quienes asisten a establecimientos educacionales públicos). Esta misma tendencia 
          se observa en cuanto a la frecuencia de la participación en actividades culturales, juego en el barrio con niñas y niños, y juego en plazas 
          y parques.`,
    link: "https://public.flourish.studio/visualisation/23731976/embed",
  },
  {
    id: "graph10",
    title: "Análisis por situación de hacinamiento",
    menuTitle: "\u2022 Hacinamiento",
    text: `Niñas y niños que viven en hacinamiento realizan menos actividades deportivas de forma semanal que quienes no viven en hacinamiento. 
          En 2024, la mayor brecha entre ambos grupos se da en la participación en actividades deportivas: mientras que un 29% de las niñas y niños 
          en hogares no hacinados ha realizado esta actividad más de 4 veces en el último mes, un 24% de las niñas y niños en hogares hacinados lo hizo.`,
    link: "https://public.flourish.studio/visualisation/23732346/embed",
  },
  {
    id: "graph11",
    title: "Análisis por disponibilidad de espacio para jugar",
    menuTitle: "\u2022 Espacio para jugar",
    text: `Las niñas y niños que no cuentan con un espacio para jugar en su hogar, realizan en menor proporción actividades deportivas en una frecuencia de 
          al menos una vez por semana (en comparación a quienes sí cuentan con este espacio, ya sea de forma permanente o transitoria). Esta misma tendencia 
          se observa en cuanto a la frecuencia de juego en el barrio con niñas y niños, y juego en plazas y parques. En cuanto a las actividades culturales, 
          quienes no tienen espacio para jugar y quienes solo disponen de uno de forma ocasional presentan niveles de participación similares entre sí, pero 
          menores a los de quienes cuentan con un espacio permanente (8% contra 13%).`,
    link: "https://public.flourish.studio/visualisation/23732442/embed",
  },
  {
    id: "graph12",
    title: "Análisis por cercanía de espacios públicos de recreación",
    menuTitle: "\u2022 Cercanía de espacios",
    text: `Las niñas y niños que viven en viviendas que se encuentran más cercanos a espacios de recreación realizan en mayor proporción actividades deportivas, 
          culturales, y juego con otras niñas y niños en el barrio, y/o en plazas y parques. En este punto, es importante recordar que un 87% de las niñas y 
          niños viven en Chile en entornos urbanos (Censo 2024), y que en estos espacios la disponibilidad de espacios de recreación se encuentra altamente 
          vinculado con el ingreso de los hogares.`,
    link: "https://public.flourish.studio/visualisation/23732227/embed",
  }
];

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ 
                color: 'black', 
                paddingLeft: ["graph1", "graph7"].includes(graph.id) ? "20px" : "30px",
              }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;
